import { formEndpoint } from '../data/formSettings';

export class SubmissionError extends Error {}

type SubmitOptions = {
  subject: string;
  replyTo?: string;
};

type ProviderResponse = {
  success?: string | boolean;
  message?: string;
};

/**
 * Sends form data to the configured form service.
 * Resolves ONLY when the service explicitly confirms success; otherwise throws.
 */
export async function submitForm(
  fields: Record<string, string>,
  { subject, replyTo }: SubmitOptions
): Promise<void> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 15000);

  try {
    const res = await fetch(formEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        ...fields,
        _subject: subject,
        _template: 'table',
        _captcha: 'false',
        ...(replyTo ? { _replyto: replyTo } : {}),
      }),
      signal: controller.signal,
    });

    let data: ProviderResponse = {};
    try {
      data = (await res.json()) as ProviderResponse;
    } catch {
      // Non-JSON response — treated as a failure below.
    }

    if (!res.ok || String(data.success) !== 'true') {
      throw new SubmissionError(
        data.message || `The form service responded with status ${res.status}.`
      );
    }
  } catch (err) {
    if (err instanceof SubmissionError) throw err;
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new SubmissionError('The request timed out.');
    }
    throw new SubmissionError('A network error prevented the form from being sent.');
  } finally {
    window.clearTimeout(timer);
  }
}
